// src/features/samplingEventForm/samplingEventUtils.js
// A small utility file to deal with some of the common functions we have populated around this feature, and I guess some of the larger functions as well.

// Pulling in constants for some of the functions
import {
  GALLONS_PER_FOOT_CONVERSION_LIST,
  DEFAULT_GALLONS_PER_FOOT,
} from "../../constants/samplingEventConstants";

/**
 * Helper function to convert time string "HH:MM" to minutes for comparison
 * @param {string} timeStr - Time string in "HH:MM" format
 * @returns {number} - Total minutes
 */
export const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
};

/**
 * Robust timestamp for sorting: combine date (UTC Y/M/D) + time (minutes)
 * @param {string} dateStr - Date string (ISO format or similar)
 * @param {string} timeStr - Time string in "HH:MM" format
 * @returns {number} - UTC timestamp in milliseconds
 */
export const getTimestamp = (dateStr, timeStr) => {
  if (!dateStr) return 0;
  try {
    const d = new Date(dateStr);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const day = d.getUTCDate();
    const minutes = timeToMinutes(timeStr || "00:00");
    const hh = Math.floor(minutes / 60);
    const mm = minutes % 60;
    return Date.UTC(y, m, day, hh, mm);
  } catch {
    return 0;
  }
};

export const formatDateForDisplay = (date) => {
  if (!date) return "";
  if (typeof date === "string") {
    // Accepts "yyyy-mm-dd" or "yyyy-mm-ddTHH:mm:ss..." etc, returns "mm/dd/yyyy"
    // Only splits on first 10 chars for safety
    const normalized = date.slice(0, 10); // Get 'YYYY-MM-DD'
    const [year, month, day] = normalized.split("-");
    if (year && month && day) {
      return `${month}/${day}/${year}`;
    }
    return date; // fallback to original string if not a standard date
  }
  return "";
};

// Function to calculate the gallons per foot based on the well casing diameter, used for Purge Volume calculations when purging 3 well volumes.
const calculateGallonsPerFoot = (wellCasingDiameterInches) => {
  // Some well casing diameters come in like this "4 / 7" so we need to split the string and take the first part, but most come in like "4"
  let diameter = parseFloat(wellCasingDiameterInches);
  if (isNaN(diameter) || diameter <= 0) {
    // Attempt to split the string and take the first part if it's a fraction
    const parts = wellCasingDiameterInches.split(" / ");
    if (parts.length > 1) {
      diameter = parseFloat(parts[0]);
    }
  }
  if (isNaN(diameter) || diameter <= 0) {
    console.warn("Invalid well casing diameter:", wellCasingDiameterInches);
    return 0;
  }

  // Pulling the conversion information from our constants list
  if (diameter in GALLONS_PER_FOOT_CONVERSION_LIST) {
    return GALLONS_PER_FOOT_CONVERSION_LIST[diameter];
    // If the diameter is not in our constants list, we will use the default conversion number
  } else {
    // After some math the conversion number is 0.0408
    const gallonsPerFoot = Math.pow(diameter, 2) * DEFAULT_GALLONS_PER_FOOT;
    //   console.log("gallonsPerFoot", gallonsPerFoot);
    return gallonsPerFoot;
  }
};

// Corrected purge volume calculation using the corrected gallons per foot function
export const calculatePurgeVolume = (
  totalDepthFt,
  depthToWaterFt,
  wellCasingDiameterInches
) => {
  const totalDepth = parseFloat(totalDepthFt);
  const depthToWater = parseFloat(depthToWaterFt);

  if (isNaN(totalDepth) || isNaN(depthToWater) || totalDepth <= depthToWater) {
    // Return 0 or null if depths are invalid or total depth is not greater than depth to water
    return null;
  }

  const gallonsPerFoot = calculateGallonsPerFoot(wellCasingDiameterInches);
  if (gallonsPerFoot === 0) {
    return null;
  }

  const waterColumnHeightFt = totalDepth - depthToWater;
  const singleWellVolumeGallons = waterColumnHeightFt * gallonsPerFoot;
  // Return the volume needed for 3 well volumes
  const purgeVolumeGallons = singleWellVolumeGallons * 3;
  return purgeVolumeGallons;
};
